// Sabit Rapor — Stok Giriş / Stok Çıkış / Stok Transferi (Belge Kayıtları).
//
// ÜÇ RAPOR TEK BİLEŞEN: üç belge türü tek entity'de tutulur (StockDocument.DocumentType
// 1=Giriş, 2=Çıkış, 3=Transfer) ve web'de de tek ekran variant prop'uyla açılır
// (StockDocumentsListPage.VARIANTS). Rapor tarafında da aynı desen izlendi — üç ayrı dosya
// yerine `variant` alan tek bileşen, AppRoutes'ta üç rota çiftine bağlanır.
//
// DEPO ALANI TÜRE GÖRE DEĞİŞİR: giriş/çıkışta tek "Depo", transferde "Kaynak Depo" +
// "Hedef Depo". Kolonların hangisinin görüneceğini variant belirler.
//
// Miktar BAZ BİRİMDEDİR (QuantityInBaseUnit) — koli/paket karışık belgede toplam anlamlı olsun diye.
//
// İki ekran (Sabit Rapor deseni):
//   1) StockDocumentCriteriaPage — tarih aralığı, Distribütör, Depo, üç durum ekseni,
//      serbest arama. Canlı "N belge kritere uyuyor" önizlemesi.
//   2) StockDocumentListPage — /api/detail-reports/stock-documents'tan belge başlıkları.
//      Grid/Grafik/Pivot ReportGrid'den.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { LIFECYCLE_STATUS_OPTIONS, APPROVAL_STATUS_OPTIONS } from './invoiceShared';
import { DateInput } from './reports/DateInput';
import { MultiSelect } from './reports/MultiSelect';

const ENDPOINT = '/api/detail-reports/stock-documents';

// Belge türüne göre değişen her şey burada; bileşenler bunu variant üzerinden okur.
const VARIANTS = {
  entry:    { documentType: 1, title: 'Stok Giriş',     code: '5120', fileName: 'stok_giris',     window: 'stokGiris5120',     transfer: false },
  exit:     { documentType: 2, title: 'Stok Çıkış',     code: '5130', fileName: 'stok_cikis',     window: 'stokCikis5130',     transfer: false },
  transfer: { documentType: 3, title: 'Stok Transferi', code: '5140', fileName: 'stok_transfer',  window: 'stokTransfer5140',  transfer: true },
};
const ROUTE_BY_VARIANT = { entry: 'stock-entry', exit: 'stock-exit', transfer: 'stock-transfer' };

// ── Yardımcılar ──
function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o); const name = optName(o); if (code && name) return `${code} - ${name}`; return name || code || '—'; };
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
const nfC = new Intl.NumberFormat('tr-TR');
const fmtCount = (v) => nfC.format(Number(v) || 0);
const fmtQty = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(Number(v) || 0));
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

function useLookups() {
  const [lk, setLk] = useState({ vendors: [], reps: [], customers: [], warehouses: [] });
  useEffect(() => {
    const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    load('/api/vendors/Select').then((vendors) => setLk((s) => ({ ...s, vendors })));
    load('/api/representatives/Select').then((reps) => setLk((s) => ({ ...s, reps })));
    load('/api/customers/Select').then((customers) => setLk((s) => ({ ...s, customers })));
    load('/api/warehouses/Select').then((warehouses) => setLk((s) => ({ ...s, warehouses })));
  }, []);
  return lk;
}

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5, width: '100%', boxSizing: 'border-box' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  countChip: { marginTop: 4, padding: '8px 16px', borderRadius: 999, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 13.5 },
};
function Field({ label, children }) { return <label style={c.field}><span style={c.label}>{label}</span>{children}</label>; }
function Select({ value, onChange, options, placeholder = 'Tümü' }) {
  return (
    <select style={c.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>{o.label !== undefined ? o.label : optLabel(o)}</option>
      ))}
    </select>
  );
}

// Belge durumu — invoiceShared.STATUS_OPTIONS ile aynı: Taslak/Onaylandı/İptal.
const ORDER_STATUS_OPTIONS = [
  { value: '0', label: 'Taslak' }, { value: '1', label: 'Onaylandı' }, { value: '2', label: 'İptal' },
];
const asOptions = (opts) => opts.map((o) => ({ value: String(o.value), label: o.label }));
const LIFECYCLE_OPTIONS = asOptions(LIFECYCLE_STATUS_OPTIONS);
const APPROVAL_OPTIONS = asOptions(APPROVAL_STATUS_OPTIONS);
function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
// ISO "yyyy-MM-dd" → "dd.MM.yyyy" (ham değer ISO kalır; grid sıralaması kronolojik olsun diye).
const fmtDate = (iso) => { if (!iso) return '—'; const [y, m, d] = String(iso).split('-'); return d ? `${d}.${m}.${y}` : iso; };
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function StockDocumentCriteriaPage({ variant = 'entry' }) {
  const cfg = VARIANTS[variant] || VARIANTS.entry;
  usePageMeta(cfg.title, 'Sabit Raporlar › Belge Kayıtları');
  const lk = useLookups();
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [vendorId, setVendorId] = useState([]);
  const [warehouseId, setWarehouseId] = useState([]);
  const [status, setStatus] = useState([]);
  const [lifecycleStatus, setLifecycleStatus] = useState([]);
  const [approvalStatus, setApprovalStatus] = useState([]);
  const [q, setQ] = useState('');
  const [preview, setPreview] = useState(null);


  const query = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    p.set('documentType', String(cfg.documentType));
    if (vendorId.length) p.set('vendorId', vendorId.join(','));
    if (warehouseId.length) p.set('warehouseId', warehouseId.join(','));
    if (status.length) p.set('status', status.join(','));
    if (lifecycleStatus.length) p.set('lifecycleStatus', lifecycleStatus.join(','));
    if (approvalStatus.length) p.set('approvalStatus', approvalStatus.join(','));
    if (q.trim()) p.set('q', q.trim());
    return p;
  }, [cfg.documentType, from, to, vendorId, warehouseId, status, lifecycleStatus, approvalStatus, q]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const p = query();
      p.set('previewOnly', 'true');
      fetch(`${ENDPOINT}?${p.toString()}`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => { if (!cancelled) setPreview(json?.data?.totals ?? json?.totals ?? null); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const clear = () => { setFrom(firstOfMonth()); setTo(today()); setVendorId([]); setWarehouseId([]); setStatus([]); setLifecycleStatus([]); setApprovalStatus([]); setQ(''); };

  const showReport = () => {
    const p = query();
    p.set('embed', 'true');
    const w = Math.min(1720, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/${ROUTE_BY_VARIANT[variant]}-list?${p.toString()}`, cfg.window,
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  };

  return (
    <div style={c.page}>
      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary }} onClick={showReport}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.grid}>
          <Field label="Başlangıç Tarihi">
            <DateInput value={from} onChange={(v) => setFrom(v)} />
          </Field>
          <Field label="Bitiş Tarihi">
            <DateInput value={to} onChange={(v) => setTo(v)} />
          </Field>
          <Field label="Distribütör"><MultiSelect value={vendorId} onChange={setVendorId} options={asChoices(lk.vendors)} /></Field>
        </div>

        <div style={c.sectionSep}><span>DİĞER</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Belge Durumu"><MultiSelect value={status} onChange={setStatus} options={ORDER_STATUS_OPTIONS} /></Field>
          <Field label="Depo"><MultiSelect value={warehouseId} onChange={setWarehouseId} options={asChoices(lk.warehouses)} /></Field>
          <Field label="Yaşam Döngüsü"><MultiSelect value={lifecycleStatus} onChange={setLifecycleStatus} options={LIFECYCLE_OPTIONS} /></Field>
          <Field label="Onay Durumu"><MultiSelect value={approvalStatus} onChange={setApprovalStatus} options={APPROVAL_OPTIONS} /></Field>
          <Field label="Belge No / Müşteri Ara">
            <input style={c.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Belge no, basılı no veya müşteri" />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 4-8 4-8-4 8-4z" /><path d="M4 11l8 4 8-4" /><path d="M4 16l8 4 8-4" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 620 }}>
            Varsayılan tarih aralığı <strong>içinde bulunulan aydır</strong>. Miktarlar
            <strong>baz birimde</strong> toplanır (koli/paket karışık belgede toplam anlamlı olsun diye).
            {cfg.transfer ? ' Transfer belgesinde depo alanı Kaynak ve Hedef olarak ikiye ayrılır.' : ''}
          </span>
          {preview && <div style={c.countChip}>{fmtCount(preview.belgeSayisi)} belge kritere uyuyor</div>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
const codeChipStyle = { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 12.5 };
function DurumChip({ v }) {
  if (!v || v === '—') return '—';
  const tone = v === 'Onaylandı' ? { bg: 'rgba(22,163,74,0.10)', fg: '#16a34a' }
    : v === 'İptal' ? { bg: 'rgba(220,38,38,0.10)', fg: '#dc2626' }
      : { bg: 'var(--bg-input, #f1f5f9)', fg: '#94a3b8' };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 12 }}>{v}</span>;
}

// Kolonlar variant'a göre kurulur: transferde tek "Depo" yerine Kaynak/Hedef ikilisi vardır.
const buildColumns = (cfg) => [
  { key: 'belgeNo', label: 'Belge No', sticky: true, cell: (row) => (row.belgeNo ? <span style={codeChipStyle}>{row.belgeNo}</span> : '—') },
  { key: 'tarih', label: 'Tarih', fmt: fmtDate },
  ...(cfg.transfer
    ? [{ key: 'kaynakDepo', label: 'Kaynak Depo', fmt: dash }, { key: 'hedefDepo', label: 'Hedef Depo', fmt: dash }]
    : [{ key: 'depo', label: 'Depo', fmt: dash }]),
  { key: 'distributor', label: 'Distribütör', fmt: dash },
  { key: 'kalemSayisi', label: 'Kalem', num: true, fmt: fmtCount },
  { key: 'urunCesidi', label: 'Ürün Çeşidi', num: true, fmt: fmtCount },
  { key: 'toplamMiktar', label: 'Toplam Miktar (baz)', num: true, fmt: fmtQty },
  { key: 'durum', label: 'Durum', cell: (row) => <DurumChip v={row.durum} /> },
  { key: 'yasamDongusu', label: 'Yaşam Döngüsü', fmt: dash },
  { key: 'onayDurumu', label: 'Onay Durumu', fmt: dash },
  { key: 'aciklama', label: 'Açıklama', fmt: dash },
];

const l = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
};
const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function printAsPdf({ rows, subtitle, title, COLUMNS }) {
  const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row) => `<tr>${COLUMNS.map((col) => {
    let v = row[col.key];
    if (col.fmt) v = col.fmt(v);
    return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v ?? '')}</td>`;
  }).join('')}</tr>`).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${title}</title><style>*{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:14px}h1{font-size:14px;color:#1e3a8a;margin:0 0 4px}.crit{font-size:9.5px;color:#475569;margin-bottom:8px}table{width:100%;border-collapse:collapse}th{background:#eef2f7;color:#475569;font-size:8px;text-transform:uppercase;padding:4px 5px;border-bottom:1.5px solid #cbd5e1}td{font-size:9px;padding:3px 5px;border-bottom:1px solid #eef2f7;white-space:nowrap}@page{size:A3 landscape;margin:10mm}</style></head><body><h1>${title}</h1><div class="crit">${hesc(subtitle)}</div><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); };
}

export function StockDocumentListPage({ variant = 'entry' }) {
  const cfg = VARIANTS[variant] || VARIANTS.entry;
  const COLUMNS = useMemo(() => buildColumns(cfg), [cfg]);
  usePageMeta(cfg.title, 'Sabit Rapor');
  const [sp] = useSearchParams();
  const lk = useLookups();

  const params = useMemo(() => {
    const o = {};
    for (const k of ['documentType', 'from', 'to', 'vendorId', 'warehouseId', 'status', 'lifecycleStatus', 'approvalStatus', 'q']) { const v = sp.get(k); if (v) o[k] = v; }
    return o;
  }, [sp]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null);
    } catch (e) {
      setError(e.message || 'Hata'); setRows([]);
    } finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportRows = visibleRows ?? rows;
  const nameById = (arr, id) => { const o = arr.find((x) => String(optId(x)) === String(id)); return o ? optName(o) : id; };
  const vendorName = params.vendorId ? nameById(lk.vendors, params.vendorId) : 'Tümü';
  const statusLabel = ORDER_STATUS_OPTIONS.find((s) => s.value === params.status)?.label ?? 'Tümü';
  const warehouseName = params.warehouseId ? nameById(lk.warehouses, params.warehouseId) : 'Tümü';
  const summaryText = `Tarih: ${fmtDate(params.from)} – ${fmtDate(params.to)} · Distribütör: ${vendorName} · Depo: ${warehouseName} · Durum: ${statusLabel}${params.q ? ` · Arama: ${params.q}` : ''}`;

  const onExcel = () => {
    const cols = COLUMNS.map((col) => ({ field: col.key, headerName: col.label, valueGetter: ({ row }) => row[col.key] }));
    exportToExcel(exportRows, cols, `${cfg.code}_${cfg.fileName}`);
  };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 4-8 4-8-4 8-4z" /><path d="M4 11l8 4 8-4" /><path d="M4 16l8 4 8-4" /></svg>
          </span>
          <div>
            <div style={l.title}>{cfg.title}</div>
            <div style={l.subtitle}>
              {loading ? 'Yükleniyor…' : `${fmtCount(totals?.belgeSayisi ?? rows.length)} belge · ${fmtCount(totals?.toplamKalem ?? 0)} kalem · ${fmtQty(totals?.toplamMiktar ?? 0)} miktar · ${nowLabel}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText, title: cfg.title, COLUMNS })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Tarih: {fmtDate(params.from)} – {fmtDate(params.to)}</span>
        <span style={l.critChip}>Distribütör: {vendorName}</span>
        <span style={l.critChip}>Depo: {warehouseName}</span>
        <span style={l.critChip}>Durum: {statusLabel}</span>
        {params.q && <span style={l.critChip}>Arama: {params.q}</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey={`stock-doc-${variant}`}
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  );
}
